import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Box, Typography, IconButton } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CategoryPopover from "./CategoryPopover";
import { getRamos } from "../../services/RamosServices";
import { getCombos } from "../../services/CombosServices";
import { getOfertasPaginate } from "../../services/OfertasServices";

export const Menu = ({ mobileOnly = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRamo, setSelectedRamo] = useState(null);
  const [selectedRubro, setSelectedRubro] = useState(null);
  const [modoExpandido, setModoExpandido] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const [ramos, setRamos] = useState([]);
  const [combos, setCombos] = useState(null);
  const [ofertas, setOfertas] = useState(null);

  useEffect(() => {
    getRamos().then(d => setRamos(Array.isArray(d) ? d : [])).catch(console.error);

    getCombos()
      .then(setCombos)
      .catch(console.error);

    getOfertasPaginate({ page: 1, perPage: 1 })
      .then(setOfertas)
      .catch(console.error);
  }, []);

  const ramosSorted = [...ramos].sort((a, b) => a.descripcion_ramo.localeCompare(b.descripcion_ramo));

  useEffect(() => { setModoExpandido(false); setSelectedRubro(null); }, [selectedRamo]);

  // ── Scroll arrows ──────────────────────────────────────────────────────────
  useEffect(() => {
    const check = () => {
      if (!scrollRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
    };
    const el = scrollRef.current;
    if (el) { check(); el.addEventListener("scroll", check); }
    window.addEventListener("resize", check);
    return () => {
      if (el) el.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [ramosSorted.length]);

  const scroll = (dir) => scrollRef.current?.scrollBy({ left: dir === "left" ? -200 : 200, behavior: "smooth" });

  const handleMenuOpen = (e) => { setMenuAnchorEl(e.currentTarget); setIsLoading(true); setTimeout(() => setIsLoading(false), 2000); };
  const handleMenuClose = () => { setMenuAnchorEl(null); setSelectedRamo(null); setModoExpandido(false); setSelectedRubro(null); };

  // ✅ nuevo param: r en lugar de idcat1
  const activeRamoId = new URLSearchParams(location.search).get("r");

  // ── Estilos ────────────────────────────────────────────────────────────────
  const pillSx = (active) => ({
    textDecoration: "none",
    color: active ? "var(--accent)" : "var(--text-secondary)",
    fontWeight: active ? 700 : 500,
    fontSize: "0.82rem", fontFamily: "var(--font-body)",
    cursor: "pointer", py: 0.6, px: 1.75,
    borderRadius: "var(--radius-full)",
    backgroundColor: active ? "var(--accent-soft)" : "transparent",
    border: active ? "1.5px solid var(--accent)" : "1.5px solid transparent",
    transition: "all 0.2s ease",
    display: "inline-block", whiteSpace: "nowrap", flexShrink: 0,
    "&:hover": {
      backgroundColor: active ? "var(--accent-soft)" : "var(--bg-subtle)",
      color: active ? "var(--accent)" : "var(--text-primary)",
      border: active ? "1.5px solid var(--accent)" : "1.5px solid var(--border-hover)",
    },
  });

  const ramoPillSx = (active) => ({
    textDecoration: "none",
    color: active ? "#fff" : "var(--text-secondary)",
    fontWeight: active ? 700 : 500,
    fontSize: "0.78rem", fontFamily: "var(--font-body)",
    cursor: "pointer", py: 0.5, px: 1.5,
    borderRadius: "var(--radius-full)",
    backgroundColor: active ? "var(--primary)" : "var(--bg-subtle)",
    border: active ? "1.5px solid var(--primary)" : "1.5px solid var(--border)",
    transition: "all 0.2s ease",
    display: "inline-block", whiteSpace: "nowrap", flexShrink: 0,
    "&:hover": { backgroundColor: "var(--primary)", color: "#fff", border: "1.5px solid var(--primary)" },
  });

  const arrowSx = (side) => ({
    position: "absolute", [side]: 0, top: "50%", transform: "translateY(-50%)",
    zIndex: 3, width: 28, height: 28,
    backgroundColor: "var(--bg-card)", border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)", borderRadius: "var(--radius-sm)",
    transition: "all 0.2s ease",
    "&:hover": { backgroundColor: "var(--accent)", borderColor: "var(--accent)", "& svg": { color: "#fff" } },
  });

  const separador = <Box sx={{ width: "1px", height: 20, backgroundColor: "var(--border)", flexShrink: 0, mx: 0.25 }} />;

  // ── Ramos scroll ───────────────────────────────────────────────────────────
  const RamosScroll = () => ramosSorted.length === 0 ? null : (
    <Box sx={{ flex: 1, position: "relative", overflow: "hidden", minWidth: 0 }}>
      {canScrollLeft && (
        <IconButton onClick={() => scroll("left")} size="small" sx={arrowSx("left")}>
          <ArrowBackIosNewIcon sx={{ fontSize: 11, color: "var(--text-muted)" }} />
        </IconButton>
      )}
      <Box ref={scrollRef} sx={{
        display: "flex", alignItems: "center", gap: 0.5,
        overflowX: "auto", px: canScrollLeft || canScrollRight ? 4 : 0.5, py: 0.25,
        scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" },
        maskImage: canScrollLeft && canScrollRight
          ? "linear-gradient(to right, transparent, black 32px, black calc(100% - 32px), transparent)"
          : canScrollLeft ? "linear-gradient(to right, transparent, black 32px)"
            : canScrollRight ? "linear-gradient(to left, transparent, black 32px)" : "none",
        WebkitMaskImage: canScrollLeft && canScrollRight
          ? "linear-gradient(to right, transparent, black 32px, black calc(100% - 32px), transparent)"
          : canScrollLeft ? "linear-gradient(to right, transparent, black 32px)"
            : canScrollRight ? "linear-gradient(to left, transparent, black 32px)" : "none",
      }}>
        {ramosSorted.map(ramo => (
          <Typography key={ramo.id}
            onClick={() => navigate(`/category?r=${ramo.id}`)}  // ✅ nuevo param
            sx={ramoPillSx(activeRamoId === String(ramo.id))}
          >
            {ramo.descripcion_ramo}
          </Typography>
        ))}
      </Box>
      {canScrollRight && (
        <IconButton onClick={() => scroll("right")} size="small" sx={arrowSx("right")}>
          <ArrowForwardIosIcon sx={{ fontSize: 11, color: "var(--text-muted)" }} />
        </IconButton>
      )}
    </Box>
  );

  const popoverProps = {
    anchorEl: menuAnchorEl, open: Boolean(menuAnchorEl),
    onClose: handleMenuClose, isLoading,
    selectedRamo, setSelectedRamo,
    selectedRubro, setSelectedRubro,
    modoExpandido, setModoExpandido,
    handleMenuClose, menuAnchorEl,
  };

  // ── Mobile only ────────────────────────────────────────────────────────────
  if (mobileOnly) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, py: 0.5, overflowX: "auto", scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}>
        <Typography variant="body2" onClick={handleMenuOpen}
          sx={pillSx((location.pathname === "/category" && !activeRamoId) || Boolean(menuAnchorEl))}>
          Categorias
        </Typography>
        <Typography component={Link} to="/ofertas" sx={pillSx(location.pathname === "/ofertas")}>Ofertas</Typography>
        {/*
        <Typography component={Link} to="/combos"  sx={pillSx(location.pathname === "/combos")}>Combos</Typography>
 */}
        {ramosSorted.length > 0 && <>{separador}<RamosScroll /></>}
        {separador}
        <Typography component={Link} to="/contacto" sx={pillSx(location.pathname === "/contacto")}>Contacto</Typography>

        <CategoryPopover {...popoverProps} />
      </Box>
    );
  }

  // ── Desktop ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: "var(--bg-card)", borderBottom: "1px solid var(--border)" }}>
      <Box sx={{ maxWidth: "var(--container-max)", mx: "auto", px: { xs: 1, md: 2 }, py: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
          <Typography variant="body2" onClick={handleMenuOpen}
            sx={pillSx((location.pathname === "/category" && !activeRamoId) || Boolean(menuAnchorEl))}>
            Categorias
          </Typography>
          {ofertas?.pagination?.totalItems > 0 && (
            <Typography component={Link} to="/ofertas" sx={pillSx(location.pathname === "/ofertas")}>Ofertas</Typography>
          )}
          {/*
          {combos?.combos?.length > 0 && (
            <Typography component={Link} to="/combos" sx={pillSx(location.pathname === "/combos")}>Combos</Typography>
          )}
             */}
        </Box>

        {ramosSorted.length > 0 && <>{separador}<RamosScroll />{separador}</>}

        <Box sx={{ flexShrink: 0 }}>
          <Typography component={Link} to="/contacto" sx={pillSx(location.pathname === "/contacto")}>Contacto</Typography>
        </Box>
      </Box>

      <CategoryPopover {...popoverProps} />
    </Box>
  );
};